import { type ComponentProps, useMemo } from 'react';

import { randomColor } from 'randomcolor';

import { Avatar, AvatarFallback, AvatarImage } from '@/shadcn/avatar';

import { isLocalNetworkUrl } from '@/utils/localNetwork';

type Props = {
  source?: string | null | undefined;
  name?: string | null | undefined;
  colorized?: boolean;
  size?: number;
} & ComponentProps<typeof Avatar>;

export const AvatarComponent = ({
  source,
  name,
  colorized = true,
  className,
  size = 32,
  ...props
}: Props) => {
  const initials = getInitials(name);
  // A local-network src makes the browser prompt for local network access
  const publicSource =
    source && !isLocalNetworkUrl(source) ? source : undefined;
  const color = useMemo(() => {
    if (!(colorized && name)) return;
    return randomColor({
      seed: name,
      luminosity: 'light',
      format: 'rgba',
      alpha: 0.2
    });
  }, [colorized, name]);

  const fontSize = useMemo(() => {
    if (size <= 0) return 0;
    if (initials?.length && initials.length > 1) {
      return size / 2.5;
    }
    return size / 1.8;
  }, [size, initials]);

  return (
    <Avatar
      className={className}
      {...props}
      style={{ width: size, height: size }}
    >
      <AvatarImage
        alt={name ? `${name}'s avatar` : 'An avatar'}
        src={publicSource}
      />
      <AvatarFallback
        delayMs={publicSource ? undefined : 0}
        style={{ backgroundColor: color }}
      >
        {initials && (
          <span
            className="mt-0.5 inline-block font-mono text-contrast/80 uppercase leading-0 tracking-widest"
            style={{ fontSize }}
          >
            {initials}
          </span>
        )}
      </AvatarFallback>
    </Avatar>
  );
};

AvatarComponent.displayName = 'AvatarComponent';

const getInitials = (name?: string | null): string | null => {
  if (!name) return null;
  const words = name.split(' ');
  const filteredWords = words.filter(word => /^[a-zA-Z0-9]+$/.test(word));
  if (filteredWords.length === 0) return null;
  return filteredWords
    .slice(0, 2)
    .map(n => n.charAt(0))
    .join('');
};
